import React from "react";
import { Translate, I18n } from "react-redux-i18n";
import { isValidUrl } from "utils/urlUtils";
import { checkPassWordEntropy, checkPassWordCharacters } from "./PasswordValidator";
import DOMPurify from "dompurify";
import store from "../store";
import {
	ProgressBar
} from "cakemail-ui-components/lib/react";

const PASSWORD_REQUIREMENTS = ["lowercase", "uppercase", "number", "symbol"];

export function isValidEmail(email, csvValidation) {
	if (csvValidation) {
		if (
			/^"*(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))"*$/.test(
				email
			)
		) {
			return true;
		}
	} else {
		if (
			/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
				email
			)
		) {
			return true;
		}
	}
	
	return false;
}

export function getInitialInputValues(inputName, initial) {
	return {
		[inputName]: initial ? initial : "",
		[inputName + "_valid"]: "",
		[inputName + "_message"]: "",
	};
}

export function validatePhone(phone, inputName) {
	let error = false;
	let message = <Translate value={"app-general-input-good"} />;
	let valid = "valid";

	if (phone !== "") {
		if (
			phone !== "" &&
			!/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/.test(phone)
		) {
			error = true;
			valid = "error";
			message = (
				<Translate
					value={"app-general-input-" + inputName + "-message-invalid"}
				/>
			);
		}
	} else {
		valid = "";
		message = "";
	}

	return {
		[inputName]: phone,
		[inputName + "_valid"]: valid,
		[inputName + "_message"]: message,
		error: error,
	};
}

export function validateUrl(url, inputName = "") {
	let error = false;
	let message = <Translate value={"app-general-input-good"} />;
	let valid = "valid";

	if (url) {
		url = url.trim();

		if (url.match(/http[s]{0,1}:\/\//) === null) {
			url = "https://" + url;
		}

		if (!isValidUrl(url)) {
			error = true;
			message = (
				<Translate
					value={"app-general-input-" + inputName + "-message-invalid"}
				/>
			);
			valid = "error";
		}
	} else {
		valid = "";
		message = "";
	}

	return {
		[inputName]: url,
		[inputName + "_valid"]: valid,
		[inputName + "_message"]: message,
		error: error,
	};
}

export function validateEmail(email, validateEmptyOnly = false) {
	let error = false;
	let isEmpty = true;
	let isValid = false;

	if (email) {
		isEmpty = email.length === 0;
		isValid = isValidEmail(email);
	}

	let message = <Translate value={"app-general-input-good"} />;
	let valid = "valid";

	if (isEmpty) {
		message = I18n._translate("app-auth_validators_email_empty");
		valid = "empty";
		error = true;
	} else if (!validateEmptyOnly && !isValid) {
		message = I18n._translate("app-auth_validators_email_invalid");
		valid = "error";
		error = true;
	}

	return {
		valid: valid,
		message: message,
		error: error,
		value: email,
	};
}

export const passReq = {
	"very_strong": { label: "very_strong", value: 128 },
	"strong": { label: "strong", value: 81 },
	"moderate": { label: "moderate", value: 65 },
	"weak": { label: "weak", value: 56 },
	"very_weak": { label: "very_weak", value: 40 },
	"too_weak": { label: "too_weak", value: 0 }
};

export function validatePassword(password, validateEmptyOnly = false, email) {
	let error = false;
	let isEmpty = true;
	const brandPassStrReq = passReq[store.getState().brand?.page_customization?.sign_up?.passwordStrengthRequirement] || passReq.very_weak;

	if (password) {
		isEmpty = password.length === 0;
	}

	let message = "";
	let valid = "valid";


	if (isEmpty) {
		//message = I18n._translate("app-auth_validators_pwd_empty");
		valid = "empty";
		error = true;
	}

	if (!validateEmptyOnly) {
		let scoreMessage = "app-auth_validators_low_score";
		let passEntropyScore = checkPassWordEntropy(password);

		const scoreLabel = Object.values(passReq).find((r) => passEntropyScore >= r.value)?.label;
		let finalScoreLabel = isEmpty ? "empty" : scoreLabel || passReq.too_weak.label;

		if (isEmpty) {
			scoreMessage = "app-auth-password-score-default-message";
		} else if (email && email.includes(password)) {
			scoreMessage = I18n._translate("app-auth_validators_pwd_in_email");
			error = true;
			valid = "error";
			finalScoreLabel = passReq.too_weak.label;
		} else if (email && password.includes(email)) {
			scoreMessage = I18n._translate("app-auth_validators_email_in_pwd");
			error = true;
			valid = "error";
			finalScoreLabel = passReq.too_weak.label;
		} else {
			if (passEntropyScore >= brandPassStrReq.value) {
				error = false;
				valid = "valid";
			} else {
				error = true;
			}

			if (scoreLabel) {
				scoreMessage = "app-auth_validators_score_" + Object.values(passReq).find((r) => passEntropyScore >= r.value)?.label;
			}

			if (["too_weak"].includes(finalScoreLabel)) {
				valid = "error";
			} else if (["very_weak", "weak", "moderate"].includes(finalScoreLabel)) {
				valid = "warning";
			}
		}


		message = <div className={`passwordProgress ${finalScoreLabel}`}>
			<Translate value={"app-auth_validators_pwd_req_explanation"} />
			<ProgressBar
				progress={passEntropyScore ? passEntropyScore * 100 / passReq.very_strong.value : 0}
				size="medium"
			/>
			<Translate className="scoreLabel" value={scoreMessage} />
		</div >;
	}

	return {
		valid,
		message,
		error,
	};
}

export function validateGenericInput(input, inputName, skipValidation = false) {
	let error = false;

	let message = <Translate value={"app-general-input-good"} />;
	let valid = "valid";

	if (!skipValidation && !isValidString(input)) {
		message = (
			<Translate value={"app-general-input-" + inputName + "-message"} />
		);
		valid = "empty";
		error = true;
	}

	return {
		[inputName]: input,
		[inputName + "_valid"]: valid,
		[inputName + "_message"]: message,
		error: error,
	};
}
export const isValidString = (string) => {
	if (string) return !/^ *$/.test(string);
	else return false;
};
/**
 * checks if the account address has missing data or not
 * @param {account data that needs to have address key} accountData
 * returns true if the account is missing data and false if the account data is proper
 */
export const checkIfAccountHasMissingInfo = (accountData) => {
	return (
		!accountData?.address?.address1 ||
		!accountData?.address?.country ||
		(!accountData?.address?.province && (accountData?.address?.country === "Canada" || accountData?.address?.country?.includes("United States")))
	);
};
export const validateSendTestEmails = (string) => {
	let emails = string
		.replace(/(\r\n|\n|\r )/gm, "")
		.trim()
		.split(",");
	let errors = [];
	let valid = "valid";

	if (string === "") {
		valid = "empty";
	}
	if (emails.length > 5) {
		valid = "error";
	} else {
		emails.forEach((email, index) => {
			const cleanedEmail = email.trim();
			if (!isValidEmail(cleanedEmail) && email !== "") {
				valid = "error";
				errors.push(cleanedEmail);
			}
		});
	}
	return {
		errors: errors.toString(),
		valid: valid,
	};
};

export function parseEmailContent(json = null, html = null) {
	let cleanJSON = json;
	let cleanHTML = html;
	if (cleanJSON) {
		//Because stringify and clean breaks the JSON, we go though the js and parse any strings
		// eslint-disable-next-line no-inner-declarations
		function eachRecursive(obj) {
			for (var k in obj) {
				if (typeof obj[k] == "object" && obj[k] !== null) eachRecursive(obj[k]);
				else {
					if (typeof obj[k] === "string") {
						obj[k] = sanitizeContent(obj[k]);
					}
				}
			}
		}

		eachRecursive(cleanJSON);
	}

	if (cleanHTML) {
		cleanHTML = sanitizeContent(html, true);
	}

	return {
		cleanHTML: "<!DOCTYPE html>" + cleanHTML,
		cleanJSON: cleanJSON,
	};
}

export function sanitizeContent(content, wholeDoc = false) {
	const param = getDomPurifySettings(wholeDoc);
	return DOMPurify.sanitize(DOMPurify.sanitize(content, param), param);
}

export function getDomPurifySettings(wholeDoc = false) {
	const param = {
		ADD_URI_SAFE_ATTR: ["property"],
		ADD_TAGS: ["#comment", "head", "meta", "link", "object"],
		ADD_ATTR: ["content", "property", "target", "http-equiv", "data"],
	};
	if (wholeDoc) {
		param.WHOLE_DOCUMENT = true;
	}
	return param;
}


export function isValidHexColor(color) {
	return /^#([0-9a-f]{3}){1,2}$/i.test(color);
}